/**
 * The guest's ordering context — doc 70 §3.
 *
 * ONE ANSWER TO "HOW DOES THIS GUEST WANT THEIR FOOD", decided once and carried
 * unchanged to the receipt.
 *
 * WHY THIS EXISTS. The mode was never chosen; it was inferred, in the middle of
 * a 3,600-line component (MenuDisplayPage `initialCustomerMode`), by a ladder
 * that read a table session, then sessionStorage, then "the best available
 * ordering mode". Two consequences, both of which testers hit:
 *
 *  - A guest who wanted to LOOK at the food was silently placed in Pickup,
 *    because `orderModes` deliberately filters Browse out of the options list.
 *    Browse became a state the product could enter but the guest could never
 *    choose. The comment there records why it was done — defaulting to Browse
 *    had shipped a menu nobody could order from — but the fix for "we guessed
 *    wrong" is to ask, not to guess differently.
 *  - A guest who wanted delivery was placed in Pickup too, and only found out
 *    at checkout.
 *
 * THE RULE. Never ask a question that has one possible answer; always ask one
 * that has more than one. `resolveArrival` below is that rule as a function.
 *
 * PURE. No React, no storage, no fetch — the persistence helpers are separate
 * and take the values explicitly. That is what makes the precedence ladder and
 * the switch-with-a-cart rule testable, which is where the real bugs live.
 *
 * @see components/place/ArrivalSheet.tsx — the UI that asks
 * @see lib/customer/place-state.ts — the sentence every screen renders from it
 */

import { placePath } from '@/lib/routing/place-href';

/** How the guest wants the food. `browse` is a real choice, not the absence of one. */
export type OrderMode = 'browse' | 'dine_in' | 'counter' | 'pickup' | 'delivery' | 'scheduled';

/** The modes that can actually produce an order. */
export const ORDERING_MODES: readonly OrderMode[] = [
  'dine_in',
  'counter',
  'pickup',
  'delivery',
  'scheduled',
] as const;

export function isOrderingMode(mode: OrderMode): boolean {
  return mode !== 'browse';
}

/**
 * Does this mode demand a signed-in customer before checkout?
 *
 * MNRQ-UX-03 (doc 71) reversed doc 70 §8: pickup and scheduled pre-orders are
 * guest-first — a name + reachable contact is what actually mitigates a no-show,
 * and an account wall in front of a guest who just wants to collect food costs
 * orders. Delivery stays account-required: a rider leaves the building with the
 * food, and a failed delivery with no verified contact has nobody to call.
 *
 * This is the MODE-ONLY default. The richer rule — QR context, merchant opt-in —
 * lives in `resolveFulfilmentPolicy` (lib/customer/fulfilment-policy.ts), which
 * the server gate and the checkout button both use so they cannot drift.
 *
 * Dine-in and counter stay guest-friendly on purpose: the QR the guest scanned
 * IS the identity, the food is coming to a table the staff can see, and a login
 * wall in front of a seated diner buys nothing.
 */
export function requiresAccount(mode: OrderMode): boolean {
  return mode === 'delivery';
}

export type OrderContext = {
  businessSlug: string;
  branchId: string | null;
  branchSlug: string | null;
  branchName: string | null;
  mode: OrderMode;
  /** Present only for a scanned seat/till. The QR decided this, not the guest. */
  session: {
    token: string;
    tableLabel: string | null;
    isCounter: boolean;
  } | null;
};

// ── Arrival ──────────────────────────────────────────────────────────────────

export type ArrivalInput = {
  /** Branches the guest could choose between. One entry means no question. */
  branches: readonly { id: string; slug: string; name: string }[];
  /** Ordering modes this branch can actually serve right now. */
  availableModes: readonly OrderMode[];
  /** Resolved from a scanned QR, if there was one. */
  session?: OrderContext['session'];
  /** A previous answer from this guest, for this branch. */
  remembered?: OrderMode | null;
  /** Branch already fixed by the URL (`/m/<business>/<branch>`). */
  branchFromUrl?: { id: string; slug: string; name: string } | null;
};

export type ArrivalDecision =
  | { ask: 'nothing'; context: Pick<OrderContext, 'branchId' | 'branchSlug' | 'branchName' | 'mode' | 'session'> }
  | { ask: 'mode'; branch: { id: string; slug: string; name: string } | null; options: OrderMode[] }
  | { ask: 'branch'; branches: readonly { id: string; slug: string; name: string }[] }
  | { ask: 'branch_and_mode'; branches: readonly { id: string; slug: string; name: string }[]; options: OrderMode[] };

/**
 * What, if anything, to ask the guest on arrival.
 *
 * Precedence, highest first — each rung exists because it removes a question the
 * guest has already answered by some other means:
 *
 *  1. A scanned QR answers BOTH. A table QR knows the branch and means dine-in;
 *     a counter QR means counter. Asking a guest standing at table 7 how they
 *     would like their food is the single most absurd thing this surface did.
 *  2. A branch in the URL answers the branch half.
 *  3. One branch is not a choice.
 *  4. One ordering mode is not a choice either — apply it and say so in the
 *     header, rather than rendering a sheet with a single button.
 *  5. A remembered answer for THIS branch is still the guest's own answer.
 *     Re-asking it is the "don't make customers repeatedly select information
 *     they already established" rule.
 *
 * Browse is appended to the options by `arrivalModeOptions`, never by the
 * availability list, so it is always offered and always last.
 */
export function resolveArrival(input: ArrivalInput): ArrivalDecision {
  const { branches, session, remembered } = input;

  // 1. The QR decided everything.
  if (session) {
    const branch = input.branchFromUrl ?? (branches.length === 1 ? branches[0] : null);
    return {
      ask: 'nothing',
      context: {
        branchId: branch?.id ?? null,
        branchSlug: branch?.slug ?? null,
        branchName: branch?.name ?? null,
        mode: session.isCounter ? 'counter' : 'dine_in',
        session,
      },
    };
  }

  const branch = input.branchFromUrl ?? (branches.length === 1 ? branches[0] : null);
  const branchSettled = Boolean(branch);
  const options = arrivalModeOptions(input.availableModes);

  // A branch that takes no orders at all: browse is the honest and only answer,
  // and it is not presented as something the guest picked.
  const orderable = input.availableModes.filter(isOrderingMode);

  const settledMode: OrderMode | null =
    orderable.length === 0
      ? 'browse'
      : remembered && input.availableModes.includes(remembered)
      ? remembered
      : orderable.length === 1
      ? orderable[0]
      : null;

  if (branchSettled && settledMode) {
    return {
      ask: 'nothing',
      context: {
        branchId: branch?.id ?? null,
        branchSlug: branch?.slug ?? null,
        branchName: branch?.name ?? null,
        mode: settledMode,
        session: null,
      },
    };
  }

  if (branchSettled) return { ask: 'mode', branch: branch ?? null, options };
  if (settledMode) return { ask: 'branch', branches };
  return { ask: 'branch_and_mode', branches, options };
}

/**
 * The options a guest is offered, with Browse always present and always last.
 *
 * Browse earns its place: "I only want to see what they do" is one of the two
 * things people come to a menu URL for, and a product that refuses to offer it
 * either guesses (what happened) or dead-ends them in a cart they never wanted.
 * Last, because the guest who wants to order should not have to read past it.
 */
export function arrivalModeOptions(available: readonly OrderMode[]): OrderMode[] {
  const ordering = ORDERING_MODES.filter((mode) => available.includes(mode));
  return [...ordering, 'browse'];
}

// ── Changing it later ────────────────────────────────────────────────────────

export type ModeChangeEffect = {
  /** Safe to apply without saying anything. */
  silent: boolean;
  /** Items survive the switch. */
  keepsCart: boolean;
  /** Mode-specific details that must be re-collected (address, slot, seat). */
  drops: ('address' | 'slot' | 'seat')[];
  /** What to tell the guest before applying it. Null when `silent`. */
  warning: string | null;
};

/**
 * What changing mode will actually do — doc 70 §3, the owner's §15.
 *
 * THE BUG THIS CLOSES. The cart is scoped by BRANCH, not by mode
 * (`cartScopeId` in PublicMenuOrderingExperience). So switching from Pickup to
 * Delivery silently re-labels the existing cart as a delivery order, keeping a
 * pickup-time choice that no longer applies and dropping the guest into
 * checkout with no address. Nothing warned them. This makes the consequence
 * explicit BEFORE the switch, which is the difference between a guest changing
 * their mind and a guest being surprised.
 *
 * A SUBMITTED order is never touched by any of this. It belongs to the context
 * it was placed in, permanently — that is what makes an order history readable.
 */
export function describeModeChange({
  from,
  to,
  cartCount,
}: {
  from: OrderMode;
  to: OrderMode;
  cartCount: number;
}): ModeChangeEffect {
  if (from === to) {
    return { silent: true, keepsCart: true, drops: [], warning: null };
  }

  // Leaving a scanned seat is the one switch that abandons a real thing: the
  // table session. The guest can always re-scan, but they must know that the
  // order will no longer reach their table.
  if ((from === 'dine_in' || from === 'counter') && isOrderingMode(to)) {
    return {
      silent: false,
      keepsCart: true,
      drops: ['seat'],
      warning:
        cartCount > 0
          ? `Your ${cartCount === 1 ? 'item' : 'items'} will move to a ${modeWord(to)} order and will no longer be sent to your table.`
          : `New orders will be ${modeWord(to)} orders, not sent to your table.`,
    };
  }

  // Into browse: nothing is lost, the cart waits. Worth saying only because the
  // guest is about to watch the ordering controls disappear.
  if (to === 'browse') {
    return {
      silent: cartCount === 0,
      keepsCart: true,
      drops: [],
      warning:
        cartCount > 0
          ? 'Your items are kept. Choose how you want them whenever you’re ready.'
          : null,
    };
  }

  const drops: ModeChangeEffect['drops'] = [];
  if (from === 'delivery') drops.push('address');
  if (from === 'scheduled') drops.push('slot');

  if (cartCount === 0 && drops.length === 0) {
    return { silent: true, keepsCart: true, drops, warning: null };
  }

  return {
    silent: false,
    keepsCart: true,
    drops,
    warning: drops.includes('address')
      ? 'Your items are kept. The delivery address you entered won’t apply, so you’ll be asked again if you switch back.'
      : drops.includes('slot')
      ? 'Your items are kept. The pickup time you chose won’t apply to this order.'
      : `Your ${cartCount === 1 ? 'item stays' : 'items stay'} in the cart.`,
  };
}

/** The guest-facing noun for a mode, for use mid-sentence. */
export function modeWord(mode: OrderMode): string {
  switch (mode) {
    case 'dine_in':
      return 'dine-in';
    case 'counter':
      return 'counter';
    case 'pickup':
      return 'pickup';
    case 'delivery':
      return 'delivery';
    case 'scheduled':
      return 'pre-order';
    default:
      return 'browsing';
  }
}

/** Title-case label for a button or chip. */
export function modeLabel(mode: OrderMode): string {
  switch (mode) {
    case 'dine_in':
      return 'Eat here';
    case 'counter':
      return 'At the counter';
    case 'pickup':
      return 'Pick up later';
    case 'delivery':
      return 'Deliver to me';
    case 'scheduled':
      return 'Pre-order';
    default:
      return 'Just looking';
  }
}

/**
 * One line saying what the mode means, for the arrival sheet's option rows.
 *
 * MNRQ-UX-02 (doc 71): each row states the practical consequence — where the
 * food goes, what the guest must provide, or what they are committing to —
 * so the guest can pick without knowing Menuraq's internal mode names.
 */
export function modeDescription(mode: OrderMode): string {
  switch (mode) {
    case 'dine_in':
      return 'Orders go to the kitchen';
    case 'counter':
      return 'Order here, collect at the till';
    case 'pickup':
      return 'Order ahead, collect in person';
    case 'delivery':
      return 'Brought to the address you give — sign-in required';
    case 'scheduled':
      return 'Choose a time to collect';
    default:
      return 'See the menu without ordering';
  }
}

// ── Persistence ──────────────────────────────────────────────────────────────

/**
 * Scoped to the BRANCH, not the menu slug.
 *
 * The old helper keyed on the menu slug, which is wrong in both directions: a
 * business whose branches share one menu had them share one mode (choosing
 * delivery at the Osu branch set delivery at the Airport branch), and a branch
 * that republished under a new slug lost the guest's answer. The branch is the
 * thing that can actually serve the food, so it is the thing the answer belongs
 * to.
 *
 * sessionStorage, not localStorage, and deliberately: "I want delivery" is true
 * for this visit. A guest who ordered delivery last month and is now standing in
 * the restaurant must not be silently put back into delivery.
 */
const MODE_KEY_PREFIX = 'menuraq:order-mode:';

export function readRememberedMode(branchId: string | null | undefined): OrderMode | null {
  if (typeof window === 'undefined' || !branchId) return null;
  try {
    const raw = sessionStorage.getItem(MODE_KEY_PREFIX + branchId);
    return isStoredMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function rememberMode(branchId: string | null | undefined, mode: OrderMode): void {
  if (typeof window === 'undefined' || !branchId) return;
  try {
    // Browse is not remembered as a preference. It is what a guest does before
    // deciding, so storing it would re-answer "how would you like this?" with
    // "they didn't want any of it" on every later visit.
    if (mode === 'browse') sessionStorage.removeItem(MODE_KEY_PREFIX + branchId);
    else sessionStorage.setItem(MODE_KEY_PREFIX + branchId, mode);
  } catch {
    /* private mode / storage disabled */
  }
}

function isStoredMode(value: string | null): value is OrderMode {
  return (
    value === 'dine_in' ||
    value === 'counter' ||
    value === 'pickup' ||
    value === 'delivery' ||
    value === 'scheduled'
  );
}

// ── Venue back target ────────────────────────────────────────────────────────

/**
 * Where "back"/"close" on a public customer transaction surface returns to —
 * the previous meaningful Menuraq task, in-app only (doc 71 MNRQ-UX-01: no
 * Chow routing, no blind `router.back()`, no blank page / marketing home).
 *
 * Identity comes from DATA, never the URL: the caller passes the slugs the
 * public wire projects from the business/branch rows it already fetched. The
 * wire emits `businessSlug` (businesses.slug) and `branchSlug`
 * (branches.public_slug) beside businessName/branchName, so this resolver is
 * the one shared way both Reserve and Book compute their in-app venue target.
 *
 * Returns `null` ONLY when `businessSlug` is missing (wire still loading or a
 * data error) — the caller keeps its existing legacy fallback for that one
 * case). A single-branch venue resolves to the venue menu `/m/<businessSlug>`;
 * a multi-branch venue to `/m/<businessSlug>/<branchSlug>`.
 */
export function venueBackTarget(input: {
  businessSlug: string | null | undefined;
  branchSlug?: string | null;
  experience?: 'v1' | 'v2' | null;
}): string | null {
  if (!input.businessSlug) return null;
  const branch = (input.branchSlug ?? '').trim();
  return placePath(
    branch ? { business: input.businessSlug, branch } : { business: input.businessSlug },
    input.experience === 'v2' ? { experience: 'v2' } : undefined,
  );
}

export type VenueBackTargetInput = Parameters<typeof venueBackTarget>[0];
